import { useQuery } from '@tanstack/react-query';
import { repoMarginCallService } from '@/services/trades/repo-margin-call-service';
import { tradesKeys } from '@/services/query-keys/trades-keys';
import type { HistoryFeedFilters, RepoMarginCall, RepoMarginCallBook } from '@/types';
import { isHistoryItemNotFound, useHistoryPages } from './use-history-pages';

/**
 * GET /repo-margin-calls/ — posted repo margin call history, newest-first (LR-479).
 *
 * Feeds the Cash Pool history drawer (one counterparty, one capped page).
 * Pass `enabled=false` to defer the fetch until the drawer is actually shown.
 */
export const useRepoMarginCallHistory = (
  counterpartyName: string | null | undefined,
  currency: string | undefined,
  enabled = true,
) => {
  return useQuery<RepoMarginCall[]>({
    queryKey: tradesKeys.repoMarginCallHistory(counterpartyName ?? '', currency ?? ''),
    queryFn: () =>
      repoMarginCallService.getHistory(counterpartyName ?? undefined, currency ?? undefined),
    enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
};

const callKey = (call: RepoMarginCall): number => call.id;

/** GET /repo-margin-calls/: the History Cash Pool feed, newest-first, paged on the server. */
export const useRepoMarginCallPages = (
  filters: HistoryFeedFilters,
  { enabled = true, keepPrevious = false }: { enabled?: boolean; keepPrevious?: boolean } = {}
) =>
  useHistoryPages<RepoMarginCall>({
    queryKey: tradesKeys.repoMarginCallPages(filters),
    fetchPage: (limit, offset) => repoMarginCallService.getHistoryPage({ ...filters, limit, offset }),
    keyOf: callKey,
    enabled,
    keepPrevious,
  });

/** A repo book is one counterparty's, whatever currency its calls are in: `?sel=` is its name. */
export const repoBookKey = (book: { counterparty_name: string }): string => book.counterparty_name;

/** GET /repo-margin-calls/by-counterparty/: one row per counterparty's repo book. */
export const useRepoMarginCallBooks = (
  filters: HistoryFeedFilters,
  { enabled = true, keepPrevious = true }: { enabled?: boolean; keepPrevious?: boolean } = {}
) =>
  useHistoryPages<RepoMarginCallBook>({
    queryKey: tradesKeys.repoMarginCallBooks(filters),
    fetchPage: (limit, offset) => repoMarginCallService.getBooksPage({ ...filters, limit, offset }),
    keyOf: repoBookKey,
    enabled,
    keepPrevious,
  });

/** GET /repo-margin-calls/{id}/: one posted call, for a deep link older than the loaded pages. */
export const useRepoMarginCall = (callId: number | undefined) =>
  useQuery<RepoMarginCall>({
    queryKey: tradesKeys.repoMarginCall(callId ?? 0),
    queryFn: () => repoMarginCallService.getCall(callId as number),
    enabled: callId !== undefined,
    staleTime: 30 * 1000,
    retry: (failureCount, error) => !isHistoryItemNotFound(error) && failureCount < 1,
  });
